import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';

const ThreatDetection = () => {
  const { token } = useAuth();
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (token) {
      fetchThreats();
    }
  }, [token]);

  const fetchThreats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/threats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setThreats(Array.isArray(data) ? data : []);
      } else {
        setThreats([]);
      }
    } catch (error) {
      console.error('Error fetching threats:', error);
      setThreats([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredThreats = threats.filter(threat => {
    if (filter === 'all') return true;
    if (filter === 'active') return threat.status === 'Active';
    if (filter === 'resolved') return threat.status === 'Resolved';
    return threat.severity === filter;
  });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'High': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'Low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cyber-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading threats...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cyber-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Threat Detection</h1>
          <p className="text-gray-400 mt-1">Monitor and manage security threats</p>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg p-6 mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', 'active', 'resolved', 'Critical', 'High', 'Medium', 'Low'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-cyber-cyan text-cyber-darker'
                    : 'bg-cyber-darker text-gray-300 hover:bg-gray-700'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm text-gray-400 mb-1">Total Threats</h3>
            <p className="text-2xl font-bold text-white">{threats.length}</p>
          </div>
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm text-gray-400 mb-1">Active Threats</h3>
            <p className="text-2xl font-bold text-red-400">
              {threats.filter(t => t.status === 'Active').length}
            </p>
          </div>
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm text-gray-400 mb-1">Critical</h3>
            <p className="text-2xl font-bold text-orange-400">
              {threats.filter(t => t.severity === 'Critical').length}
            </p>
          </div>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cyber-darker border-b border-gray-700">
                <tr>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Type</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Device</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Severity</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Status</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Detected</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredThreats.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-400">
                      No threats found
                    </td>
                  </tr>
                ) : (
                  filteredThreats.map((threat) => (
                    <tr key={threat.id} className="border-b border-gray-700 hover:bg-cyber-darker transition-colors">
                      <td className="py-4 px-4 text-sm text-white font-medium">{threat.type}</td>
                      <td className="py-4 px-4 text-sm text-gray-300">{threat.device_name || 'Unknown'}</td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 text-xs rounded border ${getSeverityColor(threat.severity)}`}>
                          {threat.severity}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          threat.status === 'Active'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {threat.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-400">
                        {new Date(threat.detected_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <button className="text-cyber-cyan hover:text-cyan-400 text-sm font-medium">
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreatDetection;